import { FC, useMemo, useCallback, useEffect, useRef, useState } from 'react'
import styles from 'components/features/coaching/advisorVideoCall/VideoCallPagePreset/VideoCallPagePreset.styl'
import { useStoreDispatch, useStoreSelector } from 'helpers/reactReduxHelpers'
import { GetMeetingVideoDetailsAction, FinishMeetingAction } from 'components/features/coaching/advisorVideoCall/types/actions'
import Button from 'components/features/pfm/Button/Button'
import LoadingOverlay from 'components/atoms/preloaders/LoadingOverlay'
import { CallPanel } from 'components/features/videoCallPanel/CallPanel/CallPanel'
import { AgoraJoinChannelAction, AgoraLeaveChannelAction, UpdateMediaPermissionsAction, AgoraReJoinChannelAction } from 'components/features/videoCalls/types/actions'
import { ControlPanel } from 'components/features/coaching/advisorVideoCall/ControlPanel/ControlPanel'
import { NotStartedYet } from 'components/features/coaching/advisorVideoCall/NotStartedYet/NotStartedYet'
import { VideoCallPagePreset } from 'components/features/coaching/advisorVideoCall/VideoCallPagePreset/VideoCallPagePreset'
import { TestMedia } from 'components/features/coaching/advisorVideoCall/TestMedia/TestMedia'
import { useBeforeUnload } from 'hooks/useBeforeUnload'
import { useMutableRef } from 'hooks/useMutableRef'
import { setUnexpectedErrorActionCreator } from 'actions/globalActions'
import { Logger as AgoraLogger } from 'agora-rtc-sdk'
import { useFullScreen } from 'hooks/useFullScreen'
import { NAReadyEventHandler } from 'handlers/NAReadyEventHandler/NAReadyEventHandler'
import { MeetingStartCountdown } from 'components/features/coaching/advisorVideoCall/MeetingStartCountdown/MeetingStartCountdown'
import { AdvisorAvatar } from 'components/features/coaching/advisor/AdvisorAvatar/AdvisorAvatar'
import { Advisor } from 'components/features/coaching/advisor/types/advisor'
import { MeetingUid } from 'components/features/coaching/advisorPage/types/interfaces'
import Logo from 'components/features/pfm/Logo/Logo'
import { isMobileSelector } from 'selectors/viewportSelectors'
import moment from 'moment'
import { ConfirmModal } from 'components/features/pfm/ConfirmModal/ConfirmModal'
import { consoleRelay } from 'components/features/webSocketLogger/ConsoleWSRelay'

export const VideoCallPage: FC = () => {
  const dispatch = useStoreDispatch()
  const meetingUid: MeetingUid | null = useMemo(() => new URLSearchParams(location.search).get('meeting_uid') || null, [])

  const { details, request } = useStoreSelector((state) => state.data.advisors.videoCallDetails)
  const advisor = details?.advisor ?? null
  const playerUid = details?.video?.connection?.uid ?? null
  const role = details?.user.role ?? null

  useMemo(() => {
    consoleRelay.setMeta({ meetingUid, playerUid, role })
  }, [meetingUid, playerUid, role])
  useMemo(() => {
    consoleRelay.start()
  }, [])


  useMemo(() => {
    AgoraLogger.enableLogUpload()
    AgoraLogger.setLogLevel(0)   // to enable all agora logs
    // AgoraLogger.setLogLevel(4)   // to disable all agora logs

  }, [])

  useMemo(() => {
    if (!meetingUid) {
      dispatch(setUnexpectedErrorActionCreator('page-not-found'))
      return
    }
    dispatch(new UpdateMediaPermissionsAction())
    dispatch(new GetMeetingVideoDetailsAction({ meetingId: meetingUid }))
  }, [dispatch, meetingUid])


  if (!meetingUid) {
    return null
  }

  if (!details) {
    return (
      <LoadingOverlay />
    )
  }

  return (
    <>
      <VideoCallPageInner advisor={ advisor } />
      { request.isLoading && (
        <LoadingOverlay />
      )}
    </>
  )
}

type VideoCallPageInnerProps = {
  advisor: Advisor | null
}
const VideoCallPageInner: FC<VideoCallPageInnerProps> = ({ advisor }) => {
  const video = useStoreSelector((state) => state.data.advisors.videoCallDetails.details?.video ?? null)
  const connection = useStoreSelector((state) => state.data.advisors.videoCallDetails.details?.video?.connection ?? null)
  const callState = useStoreSelector((state) => state.app.videoCall.client.state)
  const localStreamState = useStoreSelector((state) => state.app.videoCall.localStream.state)
  const interrupted = useStoreSelector((state) => state.app.videoCall.details.interrupted)
  const isMobile = useStoreSelector(isMobileSelector)
  const [cache, render] = useState({})

  const { isJoinable, isJoining, isJoined, isConnectable } = useMemo(() => {
    const passed = !video ? false : moment().isAfter(video.finish_time)
    const connectable = !!connection && !passed
    return {
      isJoinable: connectable && callState === 'idle',
      isJoining: connectable && callState === 'progress',
      isJoined: connectable && callState === 'active',
      isConnectable: connectable,
      isPassed: passed,
    }
  }, [callState, connection, video, cache])

  useEffect(() => {
    if (!video) {
      return
    }

    const timeLeft = moment(video.finish_time).diff(moment()) + 5000
    if (timeLeft <= 0) {
      return
    }

    const tmr = setTimeout(() => {
      render({})
    }, timeLeft)
    return () => {
      clearTimeout(tmr)
    }
  }, [video])

  const rootRef = useRef<HTMLDivElement>(null)
  const { isFullScreen, requestFullscreen, exitFullscreen } = useFullScreen(rootRef)

  useBeforeUnload(isJoined, 'Are you sure?')

  const dispatch = useStoreDispatch()

  const flagsRef = useMutableRef({ isJoining, isJoined })
  useEffect(() => {
    return () => {
      if (flagsRef.current.isJoined || flagsRef.current.isJoining) {
        dispatch(new AgoraLeaveChannelAction())
      }
    }
  }, [dispatch, flagsRef])

  const handleJoin = useCallback(() => {
    if (!connection) {
      return
    }
    dispatch(new AgoraJoinChannelAction(connection))
  }, [connection, dispatch])

  const handleLeave = useCallback(() => {
    dispatch(new FinishMeetingAction())
  }, [dispatch])

  const topCtaChild = useMemo(() => {
    if (isJoinable) {
      return (
        <Button
          onClick={ handleJoin }
          size={ isMobile ? 'small' : 'medium' }
        >
          { isMobile ? 'Join' : 'Join Session' }
        </Button>
      )
    }

    if (isJoining) {
      return (
        <Button
          onClick={ handleJoin }
          disabled
          isSubmitting
          submittingChildrenVisible
          size={ isMobile ? 'small' : 'medium' }
        >
          { isMobile ? 'Join' : 'Connecting' }
        </Button>
      )
    }

    if (isJoined) {
      return (
        <Button
          onClick={ handleLeave }
          disabled={ localStreamState === 'progress' }
        >
          { isMobile ? 'Leave' : 'Leave Session' }
        </Button>
      )
    }

    return null
  }, [handleJoin, handleLeave, isJoinable, isJoined, isJoining, localStreamState, isMobile])

  const contentChild = useMemo(() => {
    if (!isConnectable) {
      return (
        <NotStartedYet />
      )
    }

    if (isJoined) {
      return (
        <CallPanel
          isFullScreen={ isFullScreen }
          onFullScreen={ requestFullscreen }
          onExitFullScreen={ exitFullscreen }
        />
      )
    }

    return (
      <>
        <MeetingStartCountdown />
        <TestMedia />
      </>
    )
  }, [isConnectable, isJoined, requestFullscreen, exitFullscreen, isFullScreen])

  const controlChild = useMemo(() => {
    if (!isConnectable) {
      return null
    }

    return (
      <ControlPanel isFullScreen={ isFullScreen } />
    )
  }, [isConnectable, isFullScreen])

  const headerChild = useMemo(() => (
    <div className={ styles.headerWrapper }>
      <div className={ styles.logoWrapper }>
        <Logo target='dashboard' size='regular' />
      </div>
      <div className={ styles.header }>
        <div className={ styles.avatar }>
          <AdvisorAvatar uid={ !!advisor ? advisor.uid : '' } />
        </div>
        <div>
          <h1>Coaching Session</h1>
          <h2>with { advisor?.name } </h2>
        </div>
        { topCtaChild }
      </div >
    </div>
  ), [advisor, topCtaChild])

  const handleReconnect = useCallback(() => {
    dispatch(new AgoraReJoinChannelAction())
  }, [dispatch])

  return (
    <div ref={ rootRef }>
      { interrupted && (
        <ConfirmModal onConfirm={ handleReconnect } confirmButtonText='Continue'>
          Video call interrupted
        </ConfirmModal>
      )}
      <NAReadyEventHandler />
      <VideoCallPagePreset
        isFullScreen={ isFullScreen }
        header={ headerChild }
        controls={ controlChild }
      >
        { contentChild }
      </VideoCallPagePreset>
    </div>
  )
}
